import type { ArticleBlock } from '../entities/article';

const findMatches = (regex: RegExp, text: string): string[] | null => text.match(regex);

export const getWordsAndCharactersCount = (articleBody: string): { characterCount: number; wordCount: number } => {
  const htmlRegex = /(<([^>]+)>)/gi;
  const text = articleBody?.replace(htmlRegex, '');
  const words = text.trim().split(/\s+/);

  return {
    characterCount: text.replaceAll(' ', '').length,
    wordCount: words ? words.length : 0,
  };
};

export const checkIfArticleBodyIsShortherThan = (body: string, count: number) => {
  const wordCount = getWordsAndCharactersCount(body).wordCount;
  if (wordCount < count) {
    return true;
  } else {
    return false;
  }
};

export const calculateReadTime = (body: string) => {
  try {
    const trimmed = body.trim()?.replace(/['";:,.?¿\-!¡]+/g, '');
    const wordCount = trimmed?.match(/\S+/g)?.length ?? 0;
    // Set reading speed as 200 words per minute.
    const wpm = 200;
    const estimatedTime = wordCount / wpm;
    const minutes = Math.round(estimatedTime);
    return minutes < 1 ? `${Math.round(estimatedTime * 60)} seconds read` : `${minutes} min read`;
  } catch {
    return '';
  }
};

export const splitElements = (htmlString: string): string[] | null => {
  const searchTagsRegexp = /<p.*?>(.*?)<\/p.*?>/g;
  return findMatches(searchTagsRegexp, htmlString);
};

export type Tweet = {
  content: string;
  id: number;
};

/*
  We have to replace <blockquote twitter-tweet with <p id="tweet-id"></p> before inserting the Campaign.
  If this is not done, the Campaign will be inserted directly into the tweet,
  which in turn will damage the structure of the document and the tweet will not be displayed.
 */
// https://www.benzinga.com/amp/content/19518254
export const replaceAmpTwitterToP = (htmlString: string): { htmlString: string; tweets: Tweet[] } => {
  const tweets: Tweet[] = [];

  // find full twitter content
  //const ampTwitterRegex = /<blockquote[^>]*twitter-tweet[^>]*>(?<fullTweeet>.*?)<\/blockquote>/g;
  const ampTwitterRegex = /<blockquote[^>]*twitter-tweet[^>]*>(.*?)<\/blockquote>/g;
  htmlString = htmlString.replace(ampTwitterRegex, (fullMatch: string) => {
    const idMatch = fullMatch.match(/https:\/\/twitter\.com\/\S*\/(\d*)\?/);

    if (idMatch && idMatch.length > 1) {
      tweets.push({ content: fullMatch, id: Number(idMatch[1]) });
      return `<p id="${idMatch[1]}"></p>`;
    }

    return '';
  });

  return { htmlString, tweets };
};

export const replaceRedditHTML = (htmlString: string): string => {
  //const regexPattern = /<figure[^>]*?\bwp-block-embed-reddit\b[^>]*>.*?<\/figure>/s;
  const regexPattern = /<figure[^>]*?\bwp-block-embed-reddit\b[^>]*>[\s\S]*?<\/figure>/;
  htmlString = htmlString.replace(regexPattern, (...match) => {
    //const firstAnchorTag = match?.[0]?.match(/<a\b.*?<\/a>/s);
    const firstAnchorTag = match?.[0]?.match(/<a\b[\s\S]*?<\/a>/);
    if (firstAnchorTag) {
      return `<figure class="wp-block-embed is-type-rich is-provider-reddit wp-block-embed-reddit"><blockquote class="reddit-embed-bq">${firstAnchorTag}</blockquote></figure>`;
    } else {
      return match[0];
    }
  });
  return htmlString;
};

export const printProHeadlineArticleContent = () => {
  return `<p>Never miss a trade again with the fastest news alerts in the world!</p>
          <p>This headline only article is a sample of real-time intelligence
          <a href="https://benzinga.grsm.io/headlineonlyarticles3398">Benzinga Pro</a> traders use to win in the markets everyday.</p>
          <p>Want the fastest, most accurate stock market intelligence? Want EXCLUSIVE stories originated by Benzinga reporters?
          <a href="https://benzinga.grsm.io/headlineonlyarticles3398">Join 10,000+ serious traders in the Benzinga Pro community!</a>
          </p>
         `;
};

export const parseTagAttributes = (htmlString: string): RegExpMatchArray[] => {
  if (!htmlString) return [];
  return [...htmlString.matchAll(/(([a-z]*)="(.*?)")/gm)];
};

const formatParagraph = (text: string) => {
  const trimmedText = text.trim();
  const isPeriod = trimmedText[trimmedText.length - 1] === '.';
  const result = isPeriod ? trimmedText.slice(0, trimmedText.length - 1) : text;

  const htmlTagRegex = new RegExp(/(.)<[^>]*>$/, 'gi');
  const newResult = result?.replace(htmlTagRegex, '');

  return newResult;
};

export const generatePartialViewPreviewFromArticleBlocks = (blocks: ArticleBlock[]): string | null => {
  let newTeaser: null | string = null;

  const getParagraphBlocks = (blocks: ArticleBlock[]): ArticleBlock[] => {
    if (!Array.isArray(blocks)) return [];
    return blocks.filter(block => block?.tag === 'p');
  };

  const paragraphBlocks = getParagraphBlocks(blocks);

  const partialBlocks = paragraphBlocks.slice(0, 6);

  const blocksIndexes = [0, 1, 2, 3, 5];
  blocksIndexes.forEach(blockIndex => {
    if (!partialBlocks?.[blockIndex]?.innerHTML) return;
    newTeaser =
      blockIndex > 0
        ? `${newTeaser} ${formatParagraph(partialBlocks[blockIndex].innerHTML)}`
        : `${partialBlocks?.[0]?.innerHTML}`;
  });

  return newTeaser;
};

export const generatePreviewFromBody = (body: string, characterLimit = 80): string => {
  const allowedTags = ['a', 'strong', 'em', 'i', 'b', 'span', 'br'];

  const countVisibleChars = (html: string): number => {
    return html.replace(/<[^>]*>/g, '').length;
  };

  const truncateAndCombineHtml = (html: string, limit: number): string => {
    const paragraphs = html.split('</p>');
    let result = '<p>';
    let charCount = 0;
    let isTruncated = false;

    const processContent = (content: string): boolean => {
      let currentTag = '';
      let inTag = false;
      let lastWordBoundary = result.length;

      for (let i = 0; i < content.length; i++) {
        const char = content[i];

        if (char === '<') {
          inTag = true;
          currentTag = '<';
        } else if (char === '>') {
          inTag = false;
          if (currentTag.startsWith('</') || allowedTags.includes(currentTag.slice(1).split(/\s/)[0])) {
            result += currentTag + '>';
          }
          currentTag = '';
        } else if (inTag) {
          currentTag += char;
        } else {
          if (charCount >= limit) {
            isTruncated = true;
            result = result.slice(0, lastWordBoundary);
            return false;
          }
          result += char;
          charCount++;
          if (char === ' ' || char === '\n' || char === '\t' || char === '.') {
            lastWordBoundary = result.length;
          }
        }
      }
      return true;
    };

    for (let i = 0; i < paragraphs.length; i++) {
      const paragraph = paragraphs[i].replace(/<p[^>]*>/g, '').trim();
      if (paragraph) {
        if (charCount > 0) result += ' ';
        if (!processContent(paragraph)) break;
      }
    }

    if (isTruncated) {
      result = result.trim();
      if (result.endsWith('.')) {
        result = result.slice(0, -1);
      }
      result += '...';
    }

    result += '</p>';
    return result;
  };

  const cleanHtml = body.replace(/<\/?(?!p\b)[a-z](?:[^>"']|"[^"]*"|'[^']*')*>/gi, '');
  const plainTextLength = countVisibleChars(cleanHtml);

  if (plainTextLength <= characterLimit) {
    return truncateAndCombineHtml(cleanHtml, Infinity);
  } else {
    return truncateAndCombineHtml(cleanHtml, characterLimit);
  }
};

/**
 * Recursively searches through block.childBlocks to find all anchor tags and extract their hrefs
 * @param block - The ArticleBlock to search through
 * @returns Array of href strings found in anchor tags
 */
export const findAllAnchorHrefs = (block: ArticleBlock): { href: string; text: string }[] => {
  const links: { href: string; text: string }[] = [];

  const traverseBlocks = (currentBlock: ArticleBlock) => {
    if (currentBlock?.tag === 'a' && currentBlock?.tagAttributes?.href) {
      const href = currentBlock.tagAttributes.href;
      const text = currentBlock.innerHTML.replace(/<[^>]*>?/g, '');
      if (href && typeof href === 'string') {
        links.push({ href, text });
      }
    }

    if (Array.isArray(currentBlock?.childBlocks)) {
      currentBlock.childBlocks.forEach(childBlock => {
        traverseBlocks(childBlock);
      });
    }
  };

  traverseBlocks(block);
  return links;
};
